import { gql } from "@apollo/client";

export const ALL_PICTURE_FIELDS = `
  {
    id, mimeType,
    ... on PlainImage { url, imageSrc @client }
    ... on EncryptedImageV1 { url, key, iv, imageSize, imageSrc @client }
  }
`;

export const ALL_USER_FIELDS = `
  id, name, username, email, bio, pronouns { subject, object },
  picture ${ALL_PICTURE_FIELDS},
  groups {id, shortId, name, status},
  activities { id, shortId, title, creator {id, name}, location {name} },
  joinRequests { id, activity { id, shortId, title, creator { name }, location { name }, userRole } }
`;

export const ME_QUERY = gql`query Me { me { ${ALL_USER_FIELDS} } }`;

export const LOGIN_MUTATION = gql`
  mutation Login($emailOrUsername: EmailOrUsername!, $password: String!){
    login(emailOrUsername: $emailOrUsername, password: $password)
  }
`;

export const SIGNUP_MUTATION = gql`
  mutation Signup($name: String!, $username: String!, $email: Email!, $password: String!, $picture: ID){
    signup(name: $name, username: $username, email: $email, password: $password, picture: $picture)
  }
`;

export const REQUEST_PASSWORD_RESET_LINK_MUTATION = gql`
  mutation RequestPasswordResetLink($emailOrUsername: EmailOrUsername!) {
    requestPasswordResetLink(emailOrUsername: $emailOrUsername)
  }
`;

export const GET_EMAIL_FROM_RESET_TOKEN_QUERY = gql`
  query GetEmailFromResetToken($resetToken: String!) {
    getEmailFromResetToken(resetToken: $resetToken)
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($resetToken: String!, $password: String!) {
    resetPassword(resetToken: $resetToken, password: $password)
  }
`;

export const CREATE_ACTIVITY_MUTATION = gql`
mutation CreateActivity($title: String!, $description: String, $location: LocationInput!, $groups: [ID!], $startDate: DateTime, $endDate: DateTime, $visibilityStartDate: DateTime) {
  createActivity(title: $title, location: $location, description: $description, groups: $groups, startDate: $startDate, endDate: $endDate, visibilityStartDate: $visibilityStartDate) {
    id, shortId, creator {id, email}
  }
}`;

export const JOIN_REQUEST_MUTATION = gql`mutation ($activityId: ID!, $message: String!) {
  makeJoinRequest(activityId: $activityId, message: $message)
}`;

export const ACTIVITY_QUERY = gql`query ($id: ID, $shortId: String) {
  getActivity(id: $id, shortId: $shortId) {
      id
      shortId
      title
      description
      creator { id, name, username, email, picture ${ALL_PICTURE_FIELDS}, pronouns {subject, object} }
      createdAt
      location { name, geometry { coordinates } }
      groups { id, shortId, name, status }
      startDate
      endDate
      visibilityStartDate
      joinRequests { id, applicant { id, name, username }, message, status }
      userRole
      participants { username }
      picture ${ALL_PICTURE_FIELDS}
  }
}`;

export const HIDE_JOIN_REQUEST_MUTATION = gql`mutation ($id: ID!) {
  hideJoinRequest(joinRequestId: $id)
}`;

export const APPROVE_JOIN_REQUEST_MUTATION = gql`
  mutation ($joinRequestId: ID!) {
    approveJoinRequest(joinRequestId: $joinRequestId)
  }
`;

export const DELETE_ACTIVITY_MUTATION = gql`mutation ($id: ID!) {
    deleteActivity(id: $id)
}`;

export const GROUP_QUERY = gql`
  query ($id: ID, $shortId: String) {
    getGroup(id: $id, shortId: $shortId) {
        id, shortId, name, description
        creator {id, name, username, picture ${ALL_PICTURE_FIELDS}}
        activities {id, shortId, title, creator { name }, location { name }}
        memberships { user { id, name, username, picture ${ALL_PICTURE_FIELDS} }, status }
        status
    }
  }
`;

export const GROUP_DELETION_MUTATION = gql`
  mutation ($groupId: ID!) {
    deleteGroup(groupId: $groupId)
  }
`;

export const GROUP_INVITATION_ACCEPTANCE_MUTATION = gql`
  mutation ($groupId: ID!) {
    acceptGroupInvitation(groupId: $groupId)
  }
`;

export const INVITATION_MUTATION = gql`
  mutation ($groupId: ID!, $inviteeUsername: String!){
    inviteToGroup(groupId: $groupId, inviteeUsername: $inviteeUsername){
      status, user { id, name, picture ${ALL_PICTURE_FIELDS} }
    }
  }
`;

export const CREATE_IMAGE_MUTATION = gql`
    mutation CreateImage($encoding: String!, $key: String, $iv: String, $imageSize: Int, $mimeType: String!) {
        createImage(encoding: $encoding, key: $key, iv: $iv, imageSize: $imageSize, mimeType: $mimeType){
          imageId, postRequest { url, fields }
        }
    }
`;

export const CHANGE_PROFILE_PIC_MUTATION = gql`
  mutation ($newImageId: ID!) {
    changeProfilePic(newImageId: $newImageId)
  }
`;

export const CREATE_BUG_REPORT_MUTATION = gql`
  mutation CreateBugReport($title: String!, $description: String, $images: [ID!]!, $criticality: Int!) {
    createBugReport(title: $title, description: $description, images: $images, criticality: $criticality)
  }
`;

export const MY_BUG_REPORTS_QUERY = gql`
  query MyBugReports {
    me {
      id, bugReports {
        id, title, description, criticality, status, createdAt, images ${ALL_PICTURE_FIELDS}
      }
    }
  }
`;

export const ALL_BUG_REPORTS_QUERY = gql`
  query AllBugReports {
    allBugReports {
      id, title, description,
      creator {id, name, username, picture ${ALL_PICTURE_FIELDS}},
      criticality, status, createdAt,
      images ${ALL_PICTURE_FIELDS}
    }
  }
`;

export const MY_NOTIFICATIONS_QUERY = gql`
  query MyNotifications {
    me {
      id, notifications {
        id, kind, createdAt,
        ... on GroupInvitationNotification {
          invitation {
            status,
            group {
              id, shortId, name,
              creator { id, name, username, picture ${ALL_PICTURE_FIELDS} }
            }
          }
        }
        ... on NewActivityNotification {
          activity { id, shortId, title, creator { id, name, picture ${ALL_PICTURE_FIELDS} } }
        }
        ... on JoinRequestNotification {
          joinRequest {
            id,
            applicant { id, name, picture ${ALL_PICTURE_FIELDS} }
            activity { id, shortId, title }
          }
        }
        ... on JoinRequestApprovalNotification {
          joinRequest {
            id, status,
            activity { id, shortId, title, creator { id, name, pronouns { object }, picture ${ALL_PICTURE_FIELDS} } }
          }
        }
      }
    }
  }
`;